import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, User, ChevronLeft, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLogin = () => {
  const { language, t } = useLanguage();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState(null);
  const { login, logout, isAuthenticated, token } = useAuth();
  const navigate = useNavigate();

  // Fetch settings and check auth
  useEffect(() => {
    const init = async () => {
      // Fetch settings for customizable text
      try {
        const res = await axios.get(`${API}/settings`);
        setSettings(res.data);
      } catch (err) {
        console.error('Failed to fetch settings');
      }

      // Check if already authenticated and token is valid
      if (isAuthenticated && token) {
        try {
          await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          navigate('/admin/dashboard');
        } catch (err) {
          logout();
          setChecking(false);
        }
      } else {
        setChecking(false);
      }
    };
    init();
  }, [isAuthenticated, token, navigate, logout]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });
      login(response.data.access_token);
      toast.success(t('accessGranted'));
      navigate('/admin/dashboard');
    } catch (err) {
      setError(t('accessDenied'));
      toast.error(language === 'de' ? 'Zugang verweigert' : 'Access denied');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth
  if (checking) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-cyan-electric font-mono text-xl animate-pulse">
          {t('verifyingCredentials')}
        </div>
      </div>
    );
  }

  // Helper to get bilingual setting
  const getSetting = (key) => {
    const deKey = `${key}_de`;
    const enKey = `${key}_en`;
    if (language === 'de') {
      return settings?.[deKey] || settings?.[key] || '';
    }
    return settings?.[enKey] || settings?.[key] || '';
  };

  return (
    <div className="min-h-screen admin-bg flex items-center justify-center px-4">
      {/* Back to Home */}
      <Link 
        to="/" 
        className="absolute top-6 left-6 flex items-center text-slate-400 hover:text-cyan-electric transition-colors"
        data-testid="back-to-home"
      >
        <ChevronLeft className="w-5 h-5 mr-1" />
        <span className="font-rajdhani uppercase tracking-wider text-sm">{t('backToEvents')}</span>
      </Link>

      {/* Login Card */}
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 border-2 border-cyan-electric rounded-lg flex items-center justify-center holo-glow">
            <Lock className="w-8 h-8 text-cyan-electric" />
          </div>
          <h1 className="font-rajdhani text-3xl font-bold uppercase tracking-widest text-white mb-2">
            {getSetting('login_title') || t('securityClearance')}
          </h1>
          <p className="font-mono text-xs text-cyan-electric/70 uppercase tracking-wider">
            {getSetting('login_subtitle') || t('adminAccessRequired')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="glass-panel p-8 holo-border" data-testid="login-form">
          <div className="space-y-6">
            {/* Username */}
            <div className="space-y-2">
              <Label className="font-rajdhani text-xs uppercase tracking-[0.2em] text-cyan-electric/70">
                {t('identificationCode')}
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pl-10 input-command font-mono text-cyan-electric"
                  placeholder={t('enterUsername')}
                  required
                  data-testid="username-input"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label className="font-rajdhani text-xs uppercase tracking-[0.2em] text-cyan-electric/70">
                {t('securityKey')}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 input-command font-mono text-cyan-electric"
                  placeholder={t('enterPassword')}
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-alert/10 border border-red-alert/30 rounded text-red-alert text-sm" data-testid="error-message">
                <AlertTriangle className="w-4 h-4" />
                <span className="font-mono">{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-power bg-cyan-electric/10 border border-cyan-electric/50 text-cyan-electric hover:bg-cyan-electric/20 font-rajdhani uppercase tracking-wider"
              data-testid="login-submit"
            >
              {loading ? (
                <span className="animate-pulse">{t('verifying')}</span>
              ) : (
                getSetting('login_button_text') || t('requestAccess')
              )}
            </Button>
          </div>

          {/* Terminal Style Footer */}
          <div className="mt-6 pt-4 border-t border-cyan-electric/10">
            <p className="font-mono text-xs text-slate-600 text-center">
              <span className="text-cyan-electric/50">&gt;</span> {getSetting('login_protocol_text') || 'UEE Security Protocol v2.955'}
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
